import _ from 'underscore';
import React, { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { LegacyI18nContext } from '@bingads-webui-react/legacy-i18n-context';
import { Text } from '@fluentui/react';
import { ValidationGroup } from '@bingads-webui-react/primitive-validation';
import { ErrorMessages } from '@bingads-webui-react/validation';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { WebsiteInput } from './website-input';
import { websiteSchema } from './validation-schema';
import { getWebsiteAndSmartPagesStyles as getStyles, websiteAndSmartPagesStyles as styles } from './styles';
import { getIsWebsiteSocialPlatform } from './helpers';
import type { ValidationResult, WebsiteOnlyOptionProps } from '../types';

export const WebsiteOnlyOption = (props: WebsiteOnlyOptionProps) => {
  const {
    website,
    setWebsite,
    setIsValid,
    alwaysShowError,
    isWebsiteSocialPlatform,
    setIsWebsiteSocialPlatform,
    unsupportedDomainMessage,
  } = props;
  const i18n = useContext(I18nContext);
  const legacyI18n = useContext(LegacyI18nContext);
  const classes = useDefaultStyles(getStyles);

  const onValidate = useCallback((e: ValidationResult) => {
    setIsValid(e.isValid);
    setIsWebsiteSocialPlatform(e.isValid && getIsWebsiteSocialPlatform(_.get(e, ['data', 'website'])));
  }, [setIsValid, setIsWebsiteSocialPlatform]);

  return (
    <div className={classNames('website-and-smart-pages', classes.container)}>
      <div className={classNames(classes.selectedOptionContainer)}>
        <ValidationGroup
          validationProps={{
            schema: websiteSchema,
            i18n: legacyI18n,
            onValidate,
          }}
        >
          <Text variant="medium" block styles={styles.title}>
            {i18n.getString(_TL_('Your business website you want customers to visit'))}
          </Text>
          <WebsiteInput
            website={website}
            setWebsite={setWebsite}
            alwaysShowError={alwaysShowError}
            style={styles.websiteInput}
          />
          {isWebsiteSocialPlatform && <ErrorMessages messages={[unsupportedDomainMessage]} />}
        </ValidationGroup>
      </div>
    </div>);
};

WebsiteOnlyOption.propTypes = {
  website: PropTypes.string,
  setWebsite: PropTypes.func.isRequired,
  setIsValid: PropTypes.func,
  alwaysShowError: PropTypes.bool,
  isWebsiteSocialPlatform: PropTypes.bool,
  setIsWebsiteSocialPlatform: PropTypes.func,
  unsupportedDomainMessage: PropTypes.string,
};

WebsiteOnlyOption.defaultProps = {
  website: undefined,
  setIsValid: _.noop,
  alwaysShowError: false,
  unsupportedDomainMessage: null,
  isWebsiteSocialPlatform: false,
  setIsWebsiteSocialPlatform: _.noop,
};
